/**
 * SA Tools - Report Download Panel
 * Generate and download Word reports with charts
 */
import React, { useState } from 'react';
import { Paper, Title, Text, Button, Stack, Alert, Group, LoadingOverlay, Divider } from '@mantine/core';
import { IconFileTypePdf, IconFileTypeDoc, IconDownload, IconPhoto, IconFileCode, IconPackage } from '@tabler/icons-react';
import JSZip from 'jszip';
import { reportService } from '../../services/reportService';
import ComplexityImpactChart from '../charts/ComplexityImpactChart';
import ROITimelineChart from '../charts/ROITimelineChart';
import { sortAndRenumberProcesses } from '../../utils/scoreCalculator';

// Helper function to convert base64 to Blob
function base64ToBlob(base64, mimeType) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

// Download file helper
function downloadFile(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function ReportDownloadPanel({ customerId, contextData, processes }) {
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [reportUrls, setReportUrls] = useState(null);

  // Enrich processes with calculated fields for charts
  const enrichedProcesses = processes && processes.length > 0
    ? sortAndRenumberProcesses(processes)
    : [];

  const handleGenerateReport = async () => {
    if (!customerId || !contextData) {
      setError('Please save context first');
      return;
    }

    // Get processes from props or localStorage
    let processesToUse = processes;
    if (!processesToUse || processesToUse.length === 0) {
      const savedProcesses = localStorage.getItem(`processes_${customerId}`);
      if (savedProcesses) {
        processesToUse = JSON.parse(savedProcesses);
        console.log('Loaded processes from localStorage:', processesToUse.length);
      }
    }

    if (!processesToUse || processesToUse.length === 0) {
      setError('Please save the process list first');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setReportUrls(null);

    try {
      console.log('Generating report with', processesToUse.length, 'processes');

      // Enrich processes with calculated fields (total_score, weighted_score, complexity_score, etc.)
      const enrichedProcesses = sortAndRenumberProcesses(processesToUse);
      console.log('Processes enriched, first process:', enrichedProcesses[0]);

      // Call report service (now generates real Chart.js chart)
      const result = await reportService.generateReport(customerId, contextData, enrichedProcesses);

      console.log('Report generated successfully!', result);

      // Store URLs for download buttons
      setReportUrls({
        report: result.reportUrl,
        chart: result.chartUrl,
        roiTimeline: result.roiTimelineUrl,
        reportFilename: result.reportFilename,
        chartFilename: result.chartFilename,
        roiTimelineFilename: result.roiTimelineFilename
      });

      setSuccess('Report generated successfully! Use the download buttons below.');

    } catch (err) {
      console.error('Error generating report:', err);
      setError(`Failed to generate report: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateROITable = async () => {
    if (!customerId || !contextData) {
      setError('Please save context first');
      return;
    }

    let processesToUse = processes;
    if (!processesToUse || processesToUse.length === 0) {
      const savedProcesses = localStorage.getItem(`processes_${customerId}`);
      if (savedProcesses) {
        processesToUse = JSON.parse(savedProcesses);
      }
    }

    if (!processesToUse || processesToUse.length === 0) {
      setError('No processes to generate table from');
      return;
    }

    setLoadingTable(true);
    setError(null);

    try {
      console.log('Generating standalone ROI table...');

      // Enrich processes with calculated fields (total_score, weighted_score, complexity_score, etc.)
      const enrichedProcesses = sortAndRenumberProcesses(processesToUse);

      const result = await reportService.generateROITable(customerId, contextData, enrichedProcesses);

      console.log('ROI table generated successfully!', result);

      // Auto-download the ROI table
      downloadFile(result.tableUrl, result.tableFilename);

      setSuccess('ROI table downloaded successfully!');
    } catch (err) {
      console.error('Error generating ROI table:', err);
      setError(`Failed to generate ROI table: ${err.message}`);
    } finally {
      setLoadingTable(false);
    }
  };

  const handleDownloadJSON = () => {
    // Generate customer ID from context if not provided
    const exportCustomerId = customerId || contextData.customer?.toLowerCase().replace(/\s+/g, '-') || 'customer-data';

    // Create the JSON structure matching the upload format
    const exportData = {
      customerId: exportCustomerId,
      context: contextData,
      processes: processes || []
    };

    // Convert to JSON string with formatting
    const jsonString = JSON.stringify(exportData, null, 2);

    // Create blob and download
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    downloadFile(url, `${exportCustomerId}-data.json`);
    window.URL.revokeObjectURL(url);

    setSuccess('Customer data and processes downloaded as JSON!');
  };

  const handleDownloadEverything = async () => {
    if (!customerId || !contextData) {
      setError('Please save context first');
      return;
    }

    let processesToUse = processes;
    if (!processesToUse || processesToUse.length === 0) {
      const savedProcesses = localStorage.getItem(`processes_${customerId}`);
      if (savedProcesses) {
        processesToUse = JSON.parse(savedProcesses);
      }
    }

    if (!processesToUse || processesToUse.length === 0) {
      setError('Please save the process list first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Generating all files for zip package...');

      // Enrich processes
      const enrichedProcesses = sortAndRenumberProcesses(processesToUse);
      const exportCustomerId = customerId || contextData.customer?.toLowerCase().replace(/\s+/g, '-') || 'customer-data';

      // 1. Generate JSON
      const exportData = {
        customerId: exportCustomerId,
        context: contextData,
        processes: processesToUse
      };
      const jsonString = JSON.stringify(exportData, null, 2);
      const jsonBlob = new Blob([jsonString], { type: 'application/json' });

      // 2. Generate full report (includes Word doc, Process Chart, ROI Timeline Chart)
      const reportResult = await reportService.generateReport(exportCustomerId, contextData, enrichedProcesses);

      // Convert URLs to blobs
      const reportBlobResponse = await fetch(reportResult.reportUrl);
      const reportBlob = await reportBlobResponse.blob();

      const chartBlobResponse = await fetch(reportResult.chartUrl);
      const chartBlob = await chartBlobResponse.blob();

      const roiTimelineBlobResponse = await fetch(reportResult.roiTimelineUrl);
      const roiTimelineBlob = await roiTimelineBlobResponse.blob();

      // 3. Generate ROI table
      const roiTableResult = await reportService.generateROITable(exportCustomerId, contextData, enrichedProcesses);
      const roiTableBlobResponse = await fetch(roiTableResult.tableUrl);
      const roiTableBlob = await roiTableBlobResponse.blob();

      // 4. Create ZIP file
      const zip = new JSZip();
      zip.file(`${exportCustomerId}-data.json`, jsonBlob);
      zip.file(reportResult.reportFilename, reportBlob);
      zip.file(reportResult.chartFilename, chartBlob);
      zip.file(reportResult.roiTimelineFilename, roiTimelineBlob);
      zip.file(roiTableResult.tableFilename, roiTableBlob);

      // Generate and download zip
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = window.URL.createObjectURL(zipBlob);
      downloadFile(zipUrl, `${exportCustomerId}_Complete_Package.zip`);
      window.URL.revokeObjectURL(zipUrl);

      setSuccess('Complete package downloaded successfully as ZIP file!');
    } catch (err) {
      console.error('Error generating complete package:', err);
      setError(`Failed to generate complete package: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper shadow="sm" p="xl" radius="md" pos="relative">
      <LoadingOverlay visible={loading || loadingTable} />

      <Stack gap="lg">
        <div>
          <Title order={2} c="purple">Generate Report</Title>
          <Text c="dimmed" size="sm" mt="xs">
            Generate a professional Word document with all your context, processes, and ROI analysis.
            Make sure to save your process list first.
          </Text>
        </div>

        {error && (
          <Alert color="red" title="Error" onClose={() => setError(null)} withCloseButton>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="green" title="Success" onClose={() => setSuccess(null)} withCloseButton>
            {success}
          </Alert>
        )}

        {/* Download Customer Data JSON */}
        <Paper p="md" withBorder style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <div>
                <Text size="sm" fw={600}>Export Customer Data</Text>
                <Text size="xs" c="dimmed">Download all customer context and processes as JSON file</Text>
              </div>
              <Button
                leftSection={<IconFileCode size={16} />}
                onClick={handleDownloadJSON}
                color="gray"
                variant="light"
                disabled={!contextData || !contextData.customer}
              >
                Download JSON
              </Button>
            </Group>
          </Stack>
        </Paper>

        <Divider label="Generate Reports" labelPosition="center" />

        {/* Download Buttons - Show after successful report generation */}
        {reportUrls && (
          <Paper p="md" withBorder style={{ backgroundColor: 'var(--mantine-color-purple-0)' }}>
            <Stack gap="md">
              <Text size="sm" fw={600} c="purple.9">
                Download Files:
              </Text>
              <Group>
                <Button
                  leftSection={<IconFileTypeDoc size={16} />}
                  onClick={() => downloadFile(reportUrls.report, reportUrls.reportFilename)}
                  color="purple"
                  variant="light"
                  flex={1}
                >
                  IPA Report (Word)
                </Button>

                <Button
                  leftSection={<IconPhoto size={16} />}
                  onClick={() => downloadFile(reportUrls.chart, reportUrls.chartFilename)}
                  color="purple"
                  variant="light"
                  flex={1}
                >
                  Process Chart (Image)
                </Button>

                <Button
                  leftSection={<IconPhoto size={16} />}
                  onClick={() => downloadFile(reportUrls.roiTimeline, reportUrls.roiTimelineFilename)}
                  color="purple"
                  variant="light"
                  flex={1}
                >
                  ROI Timeline (Image)
                </Button>

                <Button
                  leftSection={<IconDownload size={16} />}
                  onClick={handleGenerateROITable}
                  color="purple"
                  variant="light"
                  disabled={loadingTable}
                  flex={1}
                >
                  {loadingTable ? 'Generating...' : 'ROI Table (Word)'}
                </Button>
              </Group>

              <Button
                leftSection={<IconPackage size={16} />}
                variant="gradient"
                gradient={{ from: 'indigo', to: 'blue', deg: 45 }}
                size="lg"
                onClick={handleDownloadEverything}
                disabled={loading}
                fullWidth
              >
                {loading ? 'Packaging Everything...' : "Morgan's Button - Download Everything"}
              </Button>
            </Stack>
          </Paper>
        )}

        <Stack gap="md">
          <Button
            leftSection={<IconFileTypeDoc size={16} />}
            color="purple"
            size="lg"
            onClick={handleGenerateReport}
            disabled={!customerId || loading}
            fullWidth
          >
            {loading ? 'Generating Report...' : 'Generate Full Report'}
          </Button>

          <Button
            leftSection={<IconDownload size={16} />}
            variant="light"
            color="purple"
            onClick={handleGenerateROITable}
            disabled={!customerId || loadingTable}
            fullWidth
          >
            {loadingTable ? 'Generating...' : 'Generate ROI Table Only'}
          </Button>
        </Stack>

        <Alert color="blue" title="Note">
          <Text size="sm">
            • Make sure you have saved your process list before generating reports<br/>
            • The report will include all processes with their ratings and ROI calculations<br/>
            • Reports are generated in Word document format (.docx)<br/>
            • Chart generation requires processes with complexity and impact scores
          </Text>
        </Alert>

        {!customerId && (
          <Text size="sm" c="dimmed">
            Please complete the customer context and process assessment before generating reports
          </Text>
        )}

        {/* Preview Charts */}
        {enrichedProcesses.length > 0 && (
          <>
            <Divider label="Chart Preview" labelPosition="center" mt="xl" />

            <Stack gap="xl">
              <div>
                <Title order={4} mb="md">Complexity vs Impact Chart</Title>
                <ComplexityImpactChart processes={enrichedProcesses} />
              </div>

              <div>
                <Title order={4} mb="md">ROI Timeline Chart</Title>
                <ROITimelineChart processes={enrichedProcesses} contextData={contextData} />
              </div>
            </Stack>

            <Alert color="blue">
              <Text size="sm">
                These charts will be embedded in the generated Word document.
              </Text>
            </Alert>
          </>
        )}
      </Stack>
    </Paper>
  );
}

export default ReportDownloadPanel;
